import {Box, Group, HoverCard, Paper, Stack, Text, UnstyledButton} from '@mantine/core'
import {Link} from '@tanstack/react-router'
import {format, isSameDay, startOfToday} from 'date-fns'
import type {MouseEvent} from 'react'

import {formatJobScheduleRange} from 'src/jobs/job-schedule-format'
import {
    assignmentStripDayColor,
    type AssignmentStripPresentation,
    signalInfoLinkColor,
} from 'src/theme/semantic-colors'

import {
    type AssignmentStripAssignment,
    type AssignmentStripDateRangeOptions,
    type AssignmentStripDay,
    type AssignmentStripDayStatus,
    buildAssignmentStripDays,
    DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
} from './assignment-strip-utils'

const DAY_BLOCK_MIN_HEIGHT = 28

export type AssignmentStripDayInteraction = 'popover' | 'none'

export type {AssignmentStripPresentation}

export type AssignmentStripLegendLabels = {
    unassigned: string
    bounded: string
}

export const AVAILABILITY_ASSIGNMENT_STRIP_LEGEND_LABELS: AssignmentStripLegendLabels = {
    unassigned: 'Available',
    bounded: 'Assigned',
}

export const SCHEDULE_ASSIGNMENT_STRIP_LEGEND_LABELS: AssignmentStripLegendLabels = {
    unassigned: 'Idle',
    bounded: 'On a job',
}

/** Type-fleet planned vocabulary — same schedule colors when presentation is schedule. */
export const PLANNED_ASSIGNMENT_STRIP_LEGEND_LABELS: AssignmentStripLegendLabels = {
    unassigned: 'Unscheduled',
    bounded: 'Planned',
}

type AssignmentStripProps = {
    assignments: AssignmentStripAssignment[]
    referenceDate?: Date
    referenceDayLabel?: string
    dayInteraction?: AssignmentStripDayInteraction
    dateRange?: AssignmentStripDateRangeOptions
    title?: string
    /** Optional helper under the title (e.g. clarifying bookings vs deployment). */
    description?: string
    legendLabels?: AssignmentStripLegendLabels
    presentation?: AssignmentStripPresentation
    embedded?: boolean
    showTitle?: boolean
    showLegend?: boolean
}

function defaultLegendLabels(
    presentation: AssignmentStripPresentation,
): AssignmentStripLegendLabels {
    return presentation === 'schedule'
        ? SCHEDULE_ASSIGNMENT_STRIP_LEGEND_LABELS
        : AVAILABILITY_ASSIGNMENT_STRIP_LEGEND_LABELS
}

function resolveReferenceDayLabel(referenceDate: Date, referenceDayLabel?: string) {
    if (referenceDayLabel) {
        return referenceDayLabel
    }
    if (isSameDay(referenceDate, startOfToday())) {
        return 'Today'
    }
    return format(referenceDate, 'EEE')
}

function dayColor(presentation: AssignmentStripPresentation, status: AssignmentStripDayStatus) {
    return assignmentStripDayColor(presentation, status)
}

type AssignmentStripLegendProps = {
    labels: AssignmentStripLegendLabels
    presentation: AssignmentStripPresentation
}

function AssignmentStripLegend({labels, presentation}: AssignmentStripLegendProps) {
    return (
        <Group gap="md" mb="sm">
            <LegendItem
                status="unassigned"
                color={dayColor(presentation, 'unassigned')}
                label={labels.unassigned}
            />
            <LegendItem
                status="bounded"
                color={dayColor(presentation, 'bounded')}
                label={labels.bounded}
            />
        </Group>
    )
}

type AssignmentStripDaysRowProps = {
    assignments: AssignmentStripAssignment[]
    referenceDate?: Date
    referenceDayLabel?: string
    dayInteraction?: AssignmentStripDayInteraction
    dateRange?: AssignmentStripDateRangeOptions
    presentation?: AssignmentStripPresentation
    legendLabels?: AssignmentStripLegendLabels
    onClick?: (event: MouseEvent) => void
}

export function AssignmentStripDaysRow({
    assignments,
    referenceDate = startOfToday(),
    referenceDayLabel,
    dayInteraction = 'popover',
    dateRange = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
    presentation = 'availability',
    legendLabels,
    onClick,
}: AssignmentStripDaysRowProps) {
    const days = buildAssignmentStripDays(assignments, referenceDate, dateRange)
    const resolvedReferenceDayLabel = resolveReferenceDayLabel(referenceDate, referenceDayLabel)
    const labels = legendLabels ?? defaultLegendLabels(presentation)

    return (
        <Group
            align="flex-start"
            gap="xs"
            wrap="nowrap"
            style={{overflowX: 'auto'}}
            onClick={onClick}
        >
            {days.map((day) => (
                <AssignmentStripDayColumn
                    key={day.dateKey}
                    day={day}
                    referenceDayLabel={resolvedReferenceDayLabel}
                    dayInteraction={dayInteraction}
                    presentation={presentation}
                    legendLabels={labels}
                />
            ))}
        </Group>
    )
}

export {AssignmentStripLegend}

function LegendItem({
    status,
    color,
    label,
}: {
    status: AssignmentStripDayStatus
    color: string
    label: string
}) {
    return (
        <Group gap={6} wrap="nowrap">
            <Box
                data-status={status}
                bg={color}
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: 4,
                    flexShrink: 0,
                }}
            />
            <Text size="xs" c="dimmed">
                {label}
            </Text>
        </Group>
    )
}

function formatDayHeading(date: Date) {
    return format(date, 'EEEE, MMM d, yyyy')
}

function formatAssignmentSchedule(assignment: AssignmentStripAssignment) {
    if (assignment.endDate == null) {
        return `${formatJobScheduleRange({
            startDate: assignment.startDate,
            startTime: assignment.startTime,
            endDate: null,
            endTime: null,
        })} – No end date`
    }

    return formatJobScheduleRange({
        startDate: assignment.startDate,
        startTime: assignment.startTime,
        endDate: assignment.endDate,
        endTime: null,
    })
}

function AssignmentStripDayPopover({day}: {day: AssignmentStripDay}) {
    return (
        <Stack gap="sm">
            <Text size="sm" fw={600}>
                {formatDayHeading(day.date)}
            </Text>
            {day.assignments.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No assignments
                </Text>
            ) : (
                <Stack gap="sm">
                    {day.assignments.map((assignment) => (
                        <Stack key={assignment.id} gap={2}>
                            <Text
                                component={Link}
                                to="/jobs/$jobId"
                                params={{jobId: assignment.jobId}}
                                size="sm"
                                fw={600}
                                c={signalInfoLinkColor()}
                            >
                                {assignment.jobName}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {formatAssignmentSchedule(assignment)}
                            </Text>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

function dayStatusLabel(status: AssignmentStripDayStatus, labels: AssignmentStripLegendLabels) {
    switch (status) {
        case 'unassigned':
            return labels.unassigned
        case 'bounded':
        case 'open_ended':
            return labels.bounded
    }
}

function AssignmentStripDayBlock({
    day,
    presentation,
}: {
    day: AssignmentStripDay
    presentation: AssignmentStripPresentation
}) {
    return (
        <Box
            bg={dayColor(presentation, day.status)}
            data-presentation={presentation}
            style={{
                borderRadius: 4,
                minHeight: DAY_BLOCK_MIN_HEIGHT,
                width: '100%',
            }}
        />
    )
}

type AssignmentStripDayColumnProps = {
    day: AssignmentStripDay
    referenceDayLabel: string
    dayInteraction: AssignmentStripDayInteraction
    presentation: AssignmentStripPresentation
    legendLabels: AssignmentStripLegendLabels
}

function AssignmentStripDayColumn({
    day,
    referenceDayLabel,
    dayInteraction,
    presentation,
    legendLabels,
}: AssignmentStripDayColumnProps) {
    const isReferenceDay = day.isReferenceDay
    const statusLabel = dayStatusLabel(day.status, legendLabels)

    return (
        <Box
            style={{
                flex: '1 1 0',
                minWidth: 44,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                backgroundColor: isReferenceDay ? 'var(--mantine-color-gray-1)' : undefined,
                border: isReferenceDay ? '1px solid var(--mantine-color-gray-4)' : undefined,
                borderRadius: isReferenceDay ? 4 : undefined,
                padding: 4,
            }}
        >
            <Text
                size="xs"
                fw={isReferenceDay ? 600 : undefined}
                c={isReferenceDay ? 'dark' : 'dimmed'}
                ta="center"
                lh={1.2}
            >
                {isReferenceDay ? referenceDayLabel : format(day.date, 'EEE')}
            </Text>
            <Text size="xs" c="dimmed" ta="center" lh={1.2} mb={4}>
                {format(day.date, 'M/d')}
            </Text>

            {dayInteraction === 'none' ? (
                <Box
                    aria-label={`${formatDayHeading(day.date)}. ${statusLabel}.`}
                    data-testid={`assignment-day-${day.dateKey}`}
                    data-status={day.status}
                >
                    <AssignmentStripDayBlock day={day} presentation={presentation} />
                </Box>
            ) : (
                <HoverCard width={280} shadow="sm" withArrow openDelay={200} closeDelay={100}>
                    <HoverCard.Target>
                        <UnstyledButton
                            aria-label={`${formatDayHeading(day.date)}. ${statusLabel}.`}
                            data-testid={`assignment-day-${day.dateKey}`}
                            data-status={day.status}
                            style={{width: '100%'}}
                        >
                            <AssignmentStripDayBlock day={day} presentation={presentation} />
                        </UnstyledButton>
                    </HoverCard.Target>
                    <HoverCard.Dropdown p="sm">
                        <AssignmentStripDayPopover day={day} />
                    </HoverCard.Dropdown>
                </HoverCard>
            )}
        </Box>
    )
}

export function AssignmentStrip({
    assignments,
    referenceDate = startOfToday(),
    referenceDayLabel,
    dayInteraction = 'popover',
    dateRange = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
    title = 'Assignments',
    description,
    legendLabels,
    presentation = 'availability',
    embedded = false,
    showTitle = true,
    showLegend = false,
}: AssignmentStripProps) {
    const resolvedLegendLabels = legendLabels ?? defaultLegendLabels(presentation)

    const content = (
        <>
            {showTitle ? (
                <Text fw={600} mb={description ? 4 : 'xs'}>
                    {title}
                </Text>
            ) : null}
            {description ? (
                <Text size="sm" c="dimmed" mb="xs">
                    {description}
                </Text>
            ) : null}
            {showLegend ? (
                <AssignmentStripLegend labels={resolvedLegendLabels} presentation={presentation} />
            ) : null}
            <AssignmentStripDaysRow
                assignments={assignments}
                referenceDate={referenceDate}
                referenceDayLabel={referenceDayLabel}
                dayInteraction={dayInteraction}
                dateRange={dateRange}
                presentation={presentation}
                legendLabels={resolvedLegendLabels}
            />
        </>
    )

    if (embedded) {
        return <Stack gap="xs">{content}</Stack>
    }

    return (
        <Paper withBorder p="md" radius="md">
            {content}
        </Paper>
    )
}
